import * as React from "react";
import {
  MultiSelect,
  type ItemRenderer,
  type ItemRendererProps,
} from "@blueprintjs/select";
import { MenuItem, Tag } from "@blueprintjs/core";
import { useCallback } from "react";
import type { IOrderType } from "../../../../api/admin";
import type { Folder } from "../../../../hooks";

const getOrderTypeDisplayLabel = (orderType: IOrderType): string => {
  if (orderType.site_label) {
    return `${orderType.name} - ${orderType.site_label}`;
  }
  return orderType.name;
};

const filterOrderType = (query: string, orderType: IOrderType) => {
  const searchText = `${orderType.name} ${orderType.site_label || ""}`.toLowerCase();
  return searchText.includes(query.toLowerCase());
};

const areOrderTypesEqual = (a: IOrderType, b: IOrderType) =>
  a.assignment_id != null && a.assignment_id === b.assignment_id;

const renderTag = (orderType: IOrderType) => getOrderTypeDisplayLabel(orderType);

const noResults = (
  <MenuItem disabled={true} text="No results." roleStructure="listoption" />
);

interface OrderTypeMultiSelectProps {
  folder: Folder;
  orderTypes: IOrderType[];
  readOnly: boolean;
  onChange?: (folderId: number, organizationOrderTypeIds: number[]) => void;
}

export function OrderTypeMultiSelect({
  folder,
  orderTypes,
  readOnly,
  onChange,
}: OrderTypeMultiSelectProps) {
  const getSelectedOrderTypes = useCallback(
    (folder: Folder, allOrderTypes: IOrderType[]) => {
      if (!folder.organization_order_type_ids || !allOrderTypes) return [];
      return folder.organization_order_type_ids
        .map((assignmentId) =>
          allOrderTypes.find((ot) => ot.assignment_id === assignmentId)
        )
        .filter(Boolean) as IOrderType[];
    },
    []
  );

  const handleOrderTypeRemove = useCallback(
    (orderType: IOrderType) => {
      if (!onChange) return;
      const assignmentId = orderType.assignment_id;
      if (!assignmentId) return;

      const currentIds = folder.organization_order_type_ids || [];
      const nextIds = currentIds.filter((id) => id !== assignmentId);
      onChange(folder.id, nextIds);
    },
    [folder, onChange]
  );

  const handleOrderTypeSelect = useCallback(
    (orderType: IOrderType) => {
      if (!onChange) return;
      const assignmentId = orderType.assignment_id;
      if (!assignmentId) return;

      const currentIds = folder.organization_order_type_ids || [];
      if (currentIds.includes(assignmentId)) {
        handleOrderTypeRemove(orderType);
      } else {
        onChange(folder.id, [...currentIds, assignmentId]);
      }
    },
    [folder, onChange, handleOrderTypeRemove]
  );

  const handleClear = useCallback(() => {
    if (!onChange) return;
    onChange(folder.id, []);
  }, [folder.id, onChange]);

  const renderOrderType: ItemRenderer<IOrderType> = useCallback(
    (orderType, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const selected = getSelectedOrderTypes(folder, orderTypes);
      const isSelected = selected.some(
        (selectedOrderType) =>
          selectedOrderType.assignment_id != null &&
          selectedOrderType.assignment_id === orderType.assignment_id
      );

      return (
        <MenuItem
          {...getItemProps(orderType, rendererProps)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={getOrderTypeDisplayLabel(orderType)}
          selected={isSelected}
        />
      );
    },
    [folder, orderTypes, getSelectedOrderTypes]
  );

  const selectedOrderTypes = React.useMemo(
    () => getSelectedOrderTypes(folder, orderTypes),
    [folder, orderTypes, getSelectedOrderTypes]
  );

  if (readOnly) {
    if (selectedOrderTypes.length === 0) {
      return <span style={{ color: "var(--neutral-3)" }}>—</span>;
    }

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
        {selectedOrderTypes.map((ot) => (
          <Tag key={ot.assignment_id} minimal>
            {getOrderTypeDisplayLabel(ot)}
          </Tag>
        ))}
      </div>
    );
  }

  return (
    <MultiSelect
      items={orderTypes}
      selectedItems={selectedOrderTypes}
      itemRenderer={renderOrderType}
      itemPredicate={filterOrderType}
      itemsEqual={areOrderTypesEqual}
      noResults={noResults}
      onItemSelect={handleOrderTypeSelect}
      onRemove={handleOrderTypeRemove}
      onClear={handleClear}
      placeholder="Select order types..."
      disabled={!orderTypes || orderTypes.length === 0}
      popoverProps={{
        minimal: true,
        matchTargetWidth: true,
      }}
      tagRenderer={renderTag}
      tagInputProps={{
        tagProps: {
          minimal: false,
        },
      }}
      resetOnSelect={true}
    />
  );
}

function getItemProps(
  orderType: IOrderType,
  { handleClick, handleFocus, modifiers, ref }: ItemRendererProps
) {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: orderType.assignment_id,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    text: getOrderTypeDisplayLabel(orderType),
  };
}

